import type { StripePlan } from "@gosaas/types/stripe";
import { stripePlans } from "@gosaas/types/stripe";
import {
	type ApiQueryAddonTier,
	addonQueriesForSliderIndex,
	addonTierForSliderIndex,
	selectAddonPriceId,
	sliderIndexForAddonQueries,
} from "@/utils/api-pricing";

export function formatMoney(cents: number, currency = "USD"): string {
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency,
		minimumFractionDigits: 2,
	}).format(cents / 100);
}

export function planBillingAmountCents(
	plan: StripePlan,
	isAnnual: boolean,
): number {
	if (isAnnual && plan.yearlyPrice) {
		return plan.yearlyPrice.amount;
	}
	return plan.monthlyPrice.amount;
}

export function billingIntervalLabel(isAnnual: boolean): string {
	return isAnnual ? "year" : "month";
}

export function addonAmountCents(
	index: number,
	tiers: ApiQueryAddonTier[],
	isAnnual: boolean,
): number {
	const tier = addonTierForSliderIndex(index, tiers);
	if (!tier) return 0;
	return isAnnual ? tier.yearly_cents : tier.monthly_cents;
}

export function checkoutTotalCents(
	plan: StripePlan,
	isAnnual: boolean,
	addonIndex: number,
	tiers: ApiQueryAddonTier[],
): number {
	return (
		planBillingAmountCents(plan, isAnnual) +
		addonAmountCents(addonIndex, tiers, isAnnual)
	);
}

export function checkoutAddonPriceId(
	index: number,
	tiers: ApiQueryAddonTier[],
	isAnnual: boolean,
): string | undefined {
	const tier = addonTierForSliderIndex(index, tiers);
	if (!tier) return undefined;
	return selectAddonPriceId(tier, isAnnual);
}

export function subscriptionBillingIsAnnual(
	plan: StripePlan | undefined,
	stripePriceId: string | undefined,
): boolean {
	if (!plan || !stripePriceId) return false;
	return stripePriceId === plan.yearlyPrice?.id;
}

export function managedAddonSliderIndex(
	currentAddonQueries: number,
	tiers: ApiQueryAddonTier[],
): number {
	return sliderIndexForAddonQueries(currentAddonQueries, tiers);
}

export function managedAddonIsUnchanged(
	index: number,
	tiers: ApiQueryAddonTier[],
	isAnnual: boolean,
	currentAddonQueries: number,
	currentAddonPriceId?: string,
): boolean {
	const selectedQueries = addonQueriesForSliderIndex(index, tiers);
	if (selectedQueries === 0 && currentAddonQueries === 0) {
		return true;
	}
	if (selectedQueries !== currentAddonQueries) {
		return false;
	}
	const priceId = checkoutAddonPriceId(index, tiers, isAnnual);
	return !currentAddonPriceId || currentAddonPriceId === priceId;
}

export function managedAddonApplyLabel(
	index: number,
	currentAddonQueries: number,
): string {
	if (index <= 0 && currentAddonQueries > 0) {
		return "Remove add-on";
	}
	if (index <= 0) {
		return "No add-on selected";
	}
	return "Update add-on";
}

/** Default plan selection for checkout based on team type. */
export function defaultCheckoutProductId(teamType?: string): string | null {
	const launch = stripePlans.find((p) => p.name === "Personal");
	const amplify = stripePlans.find((p) => p.name === "Team");
	if (teamType === "personal") {
		return (
			launch?.productId ??
			amplify?.productId ??
			stripePlans[0]?.productId ??
			null
		);
	}
	return amplify?.productId ?? stripePlans[0]?.productId ?? null;
}
